import * as React from 'react'
import { MutableRefObject, PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Callout, IconButton, Image, ImageLoadState, ITextField, Stack, StackItem, Text, TextField } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import Loading from '../../components/Loading'
import DocumentName from '../Render/DocumentName'
import { stealthClick } from '../StealthLink'
import useAxios from '../../hooks/useAxios'
import { Destination } from '../../context/AxiosContext'
import { DocumentNodeType, getDocumentExtension, previewFileTypes } from '../../helpers/documentHelper'
import { DocumentOpenType } from '../../context/GlobalContext'
import { useSettings } from '../../hooks/useSettings'

type Item = {
    id: string
    name: string
    type?: DocumentNodeType
}

type Props = PropsWithChildren<{
    item: Item
    targetRef: MutableRefObject<HTMLDivElement>
    visible: boolean
    hide: () => void
    reset: () => void // Callback for when the view needs to be updated.
}>

export default function DocumentPreview(props: Props): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()

    const [loaded, { setFalse: setUnloaded, setTrue: setLoaded }] = useBoolean(false)
    const [preview, setPreview] = useState<string>()
    const isValid = useMemo(() => previewFileTypes.includes(getDocumentExtension(props.item.name)), [props.item.name])
    const [isRenameMode, { setTrue: startRenameMode, setFalse: stopRenameMode }] = useBoolean(false)
    const filenameRef = useRef<ITextField>()

    const renameDocument = () => {
        axios
            .request({
                method: 'patch',
                url: `/items/${props.item.id}`,
                baseURL: Destination.Documents,
                headers: { SuppressErrors: 'true' },
                data: { name: filenameRef.current.value },
                params: { '@microsoft.graph.conflictBehavior': 'rename' },
            })
            .then(() => props.reset())
            .then(hideCallout)
    }

    const hideCallout = () => {
        stopRenameMode()
        props.hide()
    }

    useEffect(() => {
        if (props.visible && !preview && isValid) {
            axios
                .get(`/items/${props.item.id}/thumbnails/0/large`, {
                    baseURL: Destination.Documents,
                })
                .then(response => setPreview(response.data.url))
        }
    }, [props.visible])

    if (!isValid) {
        return <></>
    }

    return (
        <>
            {props.children}
            <Callout
                target={props.targetRef.current}
                calloutMaxWidth={window.innerWidth - 32}
                calloutMaxHeight={window.innerHeight - 32}
                coverTarget={false}
                gapSpace={8}
                isBeakVisible={true}
                onDismiss={hideCallout}
                hidden={!props.visible}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's2', padding: 's1' }}>
                    <Text variant="large" block nowrap styles={{ root: { flexGrow: 1, minWidth: 0 } }}>
                        {isRenameMode ? (
                            <TextField componentRef={filenameRef} defaultValue={props.item.name} />
                        ) : (
                            <DocumentName
                                id={props.item.id}
                                isLink={false}
                                name={props.item.name}
                                nodeType={props.item.type || DocumentNodeType.Document}
                                iconSize={20}
                                iconValueProps={{ textVariant: 'mediumPlus' }}
                            />
                        )}
                    </Text>
                    <StackItem disableShrink>
                        {isRenameMode ? (
                            <>
                                <IconButton title="Opslaan en sluiten" iconProps={{ iconName: 'SaveAndClose' }} onClick={renameDocument} />
                                <IconButton title="Annuleren" iconProps={{ iconName: 'Cancel' }} onClick={() => stopRenameMode()} />{' '}
                            </>
                        ) : (
                            <>
                                <IconButton title="Hernoemen" iconProps={{ iconName: 'Rename' }} onClick={() => startRenameMode()} />
                                <IconButton
                                    title="Bestand openen"
                                    iconProps={{ iconName: 'Download' }}
                                    onClick={() =>
                                        stealthClick({ id: props.item.id, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open }, axios) &&
                                        hideCallout()
                                    }
                                />
                                <IconButton title="Sluiten" iconProps={{ iconName: 'Cancel' }} onClick={hideCallout} />
                            </>
                        )}
                    </StackItem>
                </Stack>
                <StackItem>
                    {!loaded && <Loading />}
                    {preview && (
                        <Image
                            src={preview}
                            styles={{ image: { maxHeight: '100%', maxWidth: '100%' } }}
                            onLoadingStateChange={loadState => (loadState === ImageLoadState.notLoaded ? setUnloaded() : setLoaded())}
                        />
                    )}
                </StackItem>
            </Callout>
        </>
    )
}
